import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  FaEnvelope, 
  FaLock, 
  FaEye, 
  FaEyeSlash, 
  FaUserCircle, 
  FaChalkboardTeacher, 
  FaUserGraduate, 
  FaSignInAlt
} from 'react-icons/fa';

const Login = ({ setUserRole, setUserDetails }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginType, setLoginType] = useState('admin'); // 'admin' or 'student'
  const [isLoading, setIsLoading] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const navigate = useNavigate();

  // Check if user is logging out
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const logoutParam = urlParams.get('logout');
    
    if (logoutParam === 'true') {
      setLoggingOut(true);
      
      // Clear the URL parameter after a delay
      const timer = setTimeout(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
        setLoggingOut(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      if (loginType === 'admin') {
        // Admin login
        const response = await axios.post('https://apissc.sainikschoolcadet.com/api/Users/login', { email, password });
        const { u_id, f_name, email: userEmail, role } = response.data.user;
  
        let userRole = '';
        switch (role) {
          case 'admin':
            userRole = 'admin';
            break;
          case 'teacher':
            userRole = 'teacher';
            break;
          case 'counsellor':
            userRole = 'counsellor';
            break;
          default:
            throw new Error('Invalid role');
        }
  
        // Set localStorage items
        localStorage.setItem('user_id', u_id);
        localStorage.setItem('role', userRole);
        localStorage.setItem('name', f_name);
        localStorage.setItem('email', userEmail);
        localStorage.setItem('last_login', new Date().toISOString());
  
        // Set state if function exists
        if (typeof setUserRole === 'function') {
          setUserRole(userRole);
        }
  
        console.log('Admin login successful!', { u_id, f_name, userRole });
        
        // Redirect to admin dashboard
        window.location.href = '/';
      } else {
        // Student login
        console.log('Attempting student login with:', { email, password });
        const response = await axios.post('https://apissc.sainikschoolcadet.com/api/students/login', { email, password });
        console.log('Student login response:', response.data);
        
        // Extract student data from the response
        const { student } = response.data;
        
        // Set localStorage items
        localStorage.setItem('student_id', student.id);
        localStorage.setItem('role', 'student');
        localStorage.setItem('name', student.name);
        localStorage.setItem('email', student.email);
        localStorage.setItem('last_login', new Date().toISOString());
  
        // Set state if function exists
        if (typeof setUserRole === 'function') {
          setUserRole('student');
        }
  
        console.log('Student login successful!', student);
        
        // Redirect to student dashboard
        window.location.href = '/student-dashboard';
      }
    } catch (error) {
      console.error("Login error:", error);
      if (error.response) {
        console.error("Error response data:", error.response.data);
        console.error("Error response status:", error.response.status);
        setError(error.response.data.error || 'Authentication failed. Please check your credentials.');
      } else if (error.request) {
        console.error("Error request:", error.request);
        setError('No response from server. Please try again later.');
      } else {
        console.error("Error message:", error.message);
        setError('An error occurred during login. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-blue-100 to-indigo-200">
      <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md">
        {loggingOut && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md flex items-center justify-between">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
              </svg>
              <span>You have been successfully logged out.</span>
            </div>
          </div>
        )}
        
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaUserCircle className="text-5xl text-blue-500" />
          </div>
          <h2 className="text-3xl font-semibold text-gray-800">Welcome</h2>
          <p className="text-gray-600 mt-2">Login to your account</p>
        </div>
        
        <div className="flex justify-center mb-6">
          <div className="grid grid-cols-2 w-full bg-gray-100 p-1 rounded-lg gap-1">
            <button
              type="button"
              className={`px-4 py-3 rounded-md transition flex flex-col items-center ${
                loginType === 'admin' 
                  ? 'bg-blue-600 text-white shadow-md' 
                  : 'bg-transparent text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => setLoginType('admin')}
            >
              <FaChalkboardTeacher className="text-xl mb-1" />
              <span className="text-sm font-medium">Admin/Teacher</span>
            </button>
            <button
              type="button"
              className={`px-4 py-3 rounded-md transition flex flex-col items-center ${
                loginType === 'student' 
                  ? 'bg-blue-600 text-white shadow-md' 
                  : 'bg-transparent text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => setLoginType('student')}
            >
              <FaUserGraduate className="text-xl mb-1" />
              <span className="text-sm font-medium">Student</span>
            </button>
          </div>
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4 animate-fadeIn" role="alert">
            <div className="flex">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
              </svg>
              <span className="block sm:inline">{error}</span>
            </div>
          </div>
        )}
        
        <form onSubmit={handleLogin} className="space-y-6">
          <div className="relative">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaEnvelope className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={loginType === 'student' ? "student@example.com" : "admin@example.com"}
              />
            </div>
          </div>
          
          <div className="relative">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaLock className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="••••••••"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  {showPassword ? <FaEyeSlash className="h-5 w-5" /> : <FaEye className="h-5 w-5" />}
                </button>
              </div>
            </div>
          </div>

          
          
          <div>
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full flex justify-center items-center py-3 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all ${
                isLoading ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {isLoading ? (
                <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <FaSignInAlt className="mr-2" />
              )}
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
        </form>
        
      </div>
    </div>
  );
};

export default Login;